import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, RotateCcw } from 'lucide-react';
import { STEPS, getStepByNumber } from '../demo/steps';

/*
 * Arrow keys that belong to the focused control, not to the walkthrough: text
 * entry, and widgets that move with arrows themselves (tabs, sliders, radio
 * groups, lists, menus, native media controls). Page controls with their own
 * arrow handling (e.g. HospitalReports' document picker) call preventDefault,
 * which is checked too.
 */
const OWNS_ARROWS =
  'input, textarea, select, video, audio, [contenteditable]:not([contenteditable="false"]), ' +
  '[role="tablist"], [role="slider"], [role="radiogroup"], [role="listbox"], [role="menu"], ' +
  '[role="menubar"], [role="grid"], [role="tree"], [role="toolbar"], [role="spinbutton"]';

// The Contents drawer, the Corporate walkthrough and any other modal.
const OPEN_DIALOG = '[role="dialog"], [role="alertdialog"], [aria-modal="true"], dialog[open]';

/*
 * The walkthrough's Back / Next: two round arrow buttons in a small sticky
 * widget in the bottom-right margin (meeting 2026-10-20: the old full-width
 * bar covered content). Where each goes is in its accessible name and
 * tooltip. ← and → do the same from anywhere on a step page, unless the
 * focused control uses arrows itself, a modifier is held or a dialog is open.
 * Step 1 has no Back (a faded placeholder keeps Next in place); the last
 * step's Next goes back to step 1.
 */
export default function StepBar({ step }) {
  const navigate = useNavigate();
  const prev = getStepByNumber(step.n - 1);
  const next = getStepByNumber(step.n + 1);
  const nextRoute = next ? next.route : STEPS[0].route;
  const nextLabel = next ? `Next: ${next.title}` : 'Back to start';

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.target instanceof Element && e.target.closest(OWNS_ARROWS)) return;
      if (document.querySelector(OPEN_DIALOG)) return;
      if (e.key === 'ArrowLeft') {
        if (!prev) return;
        e.preventDefault();
        navigate(prev.route, { state: { via: 'step-back' } });
      } else {
        e.preventDefault();
        navigate(nextRoute);
      }
    };
    // Bubble phase on window: a control that handled the arrow itself has
    // called preventDefault by now.
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate, prev, nextRoute]);

  return (
    <nav className="stepbar" aria-label="Walkthrough steps">
      {prev ? (
        <Link
          to={prev.route}
          state={{ via: 'step-back' }}
          className="stepbar__btn stepbar__back"
          aria-label={`Back to ${prev.title}`}
          title={`Back: ${prev.title}`}
          data-trapezium-id="shell.step-back"
        >
          <ArrowLeft aria-hidden="true" size={20} />
        </Link>
      ) : (
        <span className="stepbar__btn stepbar__back is-off" aria-hidden="true">
          <ArrowLeft size={20} />
        </span>
      )}
      <Link
        to={nextRoute}
        className="stepbar__btn stepbar__next"
        aria-label={nextLabel}
        title={nextLabel}
        data-trapezium-id="shell.step-next"
      >
        {next ? <ArrowRight aria-hidden="true" size={20} /> : <RotateCcw aria-hidden="true" size={18} />}
      </Link>
    </nav>
  );
}
